import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IncorporationTimeline } from './IncorporationTimeline';
import { INCORPORATION_DESCRIPTION_MAX } from './fileRules';
import { canTrackCompany } from './eligibility';
import { IncorporationCompanyRef } from './types';
import { useIncorporationTracking } from './useIncorporationTracking';

interface Props {
  company?: IncorporationCompanyRef | null;
}

export const IncorporationAdminPanel = ({ company }: Props) => {
  const { t } = useTranslation();
  const { tracking, loading, error, refresh, advanceStep, createPendency } = useIncorporationTracking(company);
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);
  const [submitting, setSubmitting] = useState<'advance' | 'pendency' | null>(null);

  if (!company?.id) return null;

  const current = tracking?.stages.find((stage) => stage.status === 'in_progress' || stage.status === 'waiting_customer');
  const finished = Boolean(tracking && (tracking.finishedAt || tracking.stages.every((stage) => stage.status === 'completed')));
  const waiting = current?.status === 'waiting_customer';

  const showMessage = (text: string, isError: boolean) => {
    setMessage(text);
    setMessageIsError(isError);
  };

  const handleAdvance = async () => {
    if (!tracking || waiting || finished || submitting) return;
    setSubmitting('advance');
    const result = await advanceStep();
    setSubmitting(null);
    if (!result.ok) {
      showMessage(result.message || String(t('incorporation.cannotAdvance') || ''), true);
      return;
    }
    showMessage(result.message || String(t('incorporation.advanced') || ''), false);
  };

  const handlePendency = async (event: FormEvent) => {
    event.preventDefault();
    const text = description.trim();
    if (!tracking || !text || current?.status !== 'in_progress' || submitting) return;
    setSubmitting('pendency');
    const result = await createPendency(text);
    setSubmitting(null);
    if (!result.ok) {
      showMessage(result.message, true);
      return;
    }
    setDescription('');
    showMessage(result.message || String(t('incorporation.pendencySaved') || ''), false);
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
      <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
        <h2 className="text-xl font-semibold text-[#0C1FAA]">{t('incorporation.adminTitle')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('incorporation.adminHint')}</p>
      </div>
      <div className="space-y-6 p-6">
        {!canTrackCompany(company) && <p className="text-sm text-gray-600">{t('incorporation.notStarted')}</p>}
        {loading && <p className="text-sm text-gray-500">{t('incorporation.loading')}</p>}
        {error && (
          <div>
            <p className="text-sm text-red-600">{error}</p>
            <button type="button" onClick={() => void refresh()} className="mt-2 text-sm font-semibold text-[#0C1FAA] underline">
              {t('incorporation.retry')}
            </button>
          </div>
        )}
        {tracking && (
          <>
            <IncorporationTimeline tracking={tracking} />
            {!finished && (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900">{t('incorporation.advance')}</h3>
                  <p className="mt-1 text-sm text-gray-600">{t('incorporation.cannotSkip')}</p>
                  <button
                    type="button"
                    onClick={() => void handleAdvance()}
                    disabled={waiting || !current || submitting !== null}
                    className="mt-4 rounded-lg bg-[#0C1FAA] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {submitting === 'advance' ? t('incorporation.sending') : t('incorporation.advance')}
                  </button>
                  {waiting && <p className="mt-2 text-sm text-amber-700">{t('incorporation.cannotAdvanceWaiting')}</p>}
                </div>
                <form onSubmit={(event) => void handlePendency(event)} className="rounded-lg border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900">{t('incorporation.registerPendency')}</h3>
                  <textarea
                    value={description}
                    maxLength={INCORPORATION_DESCRIPTION_MAX}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder={t('incorporation.pendencyPlaceholder') || ''}
                    className="mt-3 min-h-[96px] w-full rounded-lg border border-gray-300 p-3 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={current?.status !== 'in_progress' || !description.trim() || submitting !== null}
                    className="mt-3 rounded-lg border border-[#0C1FAA] px-4 py-2 text-sm font-semibold text-[#0C1FAA] disabled:opacity-50"
                  >
                    {submitting === 'pendency' ? t('incorporation.sending') : t('incorporation.registerPendency')}
                  </button>
                </form>
              </div>
            )}
            {message && <p className={`text-sm ${messageIsError ? 'text-red-600' : 'text-gray-700'}`}>{message}</p>}
          </>
        )}
      </div>
    </section>
  );
};
